import {PurchasePage} from '../purchasePage';
import {
  ISessionService,
  ProductInfoV2,
  SessionInfo,
} from '../../../services/session';
import {
  PURCHASE_PAGE_CHECKOUT,
  PURCHASE_PAGE_LOGIN,
} from '../../../config/paths';
import {ProductsSelector} from './productsSelector';
import {HtmlHelper} from '../../../services/htmlHelper';
import {
  ApiContractBlueprintGroupWithPerkbase,
  ApiContractBlueprintWithPerk,
  IPurchaseService,
} from '../../../services/purchase';
import {
  BLUEPRINT_VARIANT,
  DISCOUNT_CODE_APPLIED,
} from '../../../const/queryParameters';
import {
  PURCHASE_PAGE_ACTIONS_HTML_BINDING,
  PURCHASE_PAGE_HTML_BINDING,
  PRODUCT_SELECT_ACTIONS_HTML_BINDING,
} from '../../../const/htmlBindings';
import {
  HTML_ATTR_ACTION,
  HTML_ATTR_INPUT,
  HTML_ATTR_RENDERIF,
} from '../../../const/html';
import {setOnClick, setOnFormSubmit} from '../../../services/html';
import {EnvConfig} from '../../../config/envConfig.interface';
import {AuthService} from '../../../services/authService';
import {IUserService} from '../../../services/user';
import {ITrackingService} from '../../../services/gtmHelper';
import {IBindingService} from '../../../services/bindingService';
import {ITranslateService} from '../../../services/translateService';
import {ApiError} from '../../../services/api';

export type ProductPageConfig = {
  /**
   * Default blueprint variant (may be overridden via a URL parameter).
   * If not set the default (in config file) will be used.
   */
  defaultBlueprintVariant?: string;
};

export class ProductPage extends PurchasePage {
  private allProductSelectors: ProductsSelector[] = [];
  private discountField!: HTMLInputElement | null;
  private applyDiscountElement!: HTMLElement | null;

  constructor(
    protected productPageConfig: ProductPageConfig,
    configuration: EnvConfig,
    authService: AuthService,
    translateService: ITranslateService,
    sessionService: ISessionService,
    userService: IUserService,
    trackingService: ITrackingService,
    purchaseService: IPurchaseService,
    bindingService: IBindingService,
  ) {
    super(
      configuration,
      authService,
      translateService,
      sessionService,
      userService,
      trackingService,
      purchaseService,
      bindingService,
    );
  }

  public render(): void {
    this.parseContent();
  }

  public afterUserLoggedIn(): void {
    this.authService.onAuthStateChanged(async () => {
      await this.fetchBlueprintsAndRenderProductSelectors();
    });
  }

  private parseContent(): void {
    this.analyzeDiscountField();
    this.analyzeProductTemplates();
    this.analyzeProceedAction();
  }

  private analyzeDiscountField(): void {
    this.discountField = this.getDiscountField();
    this.applyDiscountElement = this.getApplyDiscountButton();

    if (this.discountField && this.applyDiscountElement) {
      if (this.applyDiscountElement.tagName === 'BUTTON') {
        setOnClick(this.applyDiscountElement, () => {
          const discountCode = this.discountField?.value;
          if (discountCode) {
            this.applyDiscount(discountCode);
          }
        });
      } else if (this.applyDiscountElement.tagName === 'FORM') {
        setOnFormSubmit(this.applyDiscountElement, () => {
          const discountCode = this.discountField?.value;
          if (discountCode) {
            this.applyDiscount(discountCode);
          }
        });
      }
    }
  }

  private getDiscountField(): HTMLInputElement | null {
    const discountField = HtmlHelper.instance.getElementByDataAttribute(
      HTML_ATTR_INPUT,
      PURCHASE_PAGE_HTML_BINDING.discountField,
    );

    if (
      discountField &&
      discountField !== null &&
      discountField.tagName === 'INPUT'
    ) {
      return discountField as HTMLInputElement;
    } else {
      console.warn('discount field should be an input');
      return null;
    }
  }

  private getApplyDiscountButton(): HTMLElement | null {
    return HtmlHelper.instance.getElementByDataAttribute(
      HTML_ATTR_ACTION,
      PURCHASE_PAGE_ACTIONS_HTML_BINDING.applyDiscount,
    );
  }

  private analyzeProceedAction(): void {
    // we do it to support product actions outside of the product selector.
    // We may assume that the product was already selected and this should
    // redirect to proceed the flow.
    const actionElements = HtmlHelper.instance.getElementsByDataAttribute(
      HTML_ATTR_ACTION,
      PRODUCT_SELECT_ACTIONS_HTML_BINDING.selectProductWithRedirect,
    );

    for (const actionElement of actionElements) {
      setOnClick(actionElement, () => {
        const selectedProduct = this.sessionService.getObject<ProductInfoV2>(
          SessionInfo.purchase_productInfoV2,
        );
        if (selectedProduct === null) {
          console.error('product is not selected!');
        } else {
          void this.redirectAfterProductSelect();
        }
      });
    }
  }

  private analyzeProductTemplates(): void {
    const selectorNodes = HtmlHelper.instance.document.querySelectorAll(
      '.sc-lp-purchase-product-selector',
    );

    for (let i = 0; i < selectorNodes.length; i++) {
      this.allProductSelectors.push(
        new ProductsSelector(
          selectorNodes[i] as HTMLElement,
          this.translateService,
        ),
      );
    }
  }

  private applyDiscount(discountCode: string): void {
    // redirect with the blueprint variant applied
    HtmlHelper.instance.window.location.search =
      HtmlHelper.instance.buildSearchQuery(
        HtmlHelper.instance.window.location.search,
        {
          [BLUEPRINT_VARIANT]: discountCode.toLowerCase(),
          [DISCOUNT_CODE_APPLIED]: true,
        },
      );
  }

  /********************
   * RENDER PRODUCTS
   */

  private async fetchBlueprintsAndRenderProductSelectors(): Promise<void> {
    for (const productSelector of this.allProductSelectors) {
      productSelector.startLoadingState();
    }

    try {
      let variantOverride =
        HtmlHelper.instance.getUrlQueryParam(BLUEPRINT_VARIANT);
      const discountCodeWasUsed =
        HtmlHelper.instance.getUrlQueryParam(DISCOUNT_CODE_APPLIED) === 'true';

      // if no override provided in URL
      // check if variant should be maintained from previous page and get it from session (landingpage can decide whether to maintain or not)
      if (
        variantOverride === null &&
        this.sessionService.getBoolFallback(
          SessionInfo.purchase_maintainBpVariant,
          false,
        )
      ) {
        variantOverride = this.sessionService.get(
          SessionInfo.purchase_blueprintVariant,
        );
      }

      // if variant override is still null, get default configured for this page (if configured)
      if (
        variantOverride === null &&
        this.productPageConfig.defaultBlueprintVariant
      ) {
        variantOverride =
          this.productPageConfig.defaultBlueprintVariant ?? null;
      }

      const getProductsResponse =
        await this.purchaseService.getProducts(variantOverride);
      const bpGroups = getProductsResponse.blueprintGroups;
      const variantUsed = getProductsResponse.variantUsed;

      // we check if the discount code was explictly used
      // so we don't show discount applied message
      // when a user comes from a campaign
      if (discountCodeWasUsed && variantUsed === variantOverride) {
        this.showDiscountWasApplied();
      } else if (discountCodeWasUsed && variantUsed !== variantOverride) {
        this.showDiscountFailed();
      }

      for (const productSelector of this.allProductSelectors) {
        productSelector.renderProducts({
          actions: {
            selectProductAndRedirect: this.selectProductAndProceed.bind(this),
            selectProduct: this.selectBlueprint.bind(this),
          },
          blueprintGroups: bpGroups,
        });
      }
    } catch (err) {
      const errorTyped = err as Error | ApiError | string;
      for (const productSelector of this.allProductSelectors) {
        await productSelector.setError(errorTyped);
      }
    }
  }

  private showDiscountWasApplied() {
    const discountAppliedSuccessMessage =
      HtmlHelper.instance.getElementByDataAttribute(
        HTML_ATTR_RENDERIF,
        PURCHASE_PAGE_HTML_BINDING.discountAppliedSuccess,
      );
    if (discountAppliedSuccessMessage) {
      HtmlHelper.instance.showElement(discountAppliedSuccessMessage);
    }
  }

  private showDiscountFailed() {}

  private async selectProductAndProceed(
    group: ApiContractBlueprintGroupWithPerkbase,
    blueprint: ApiContractBlueprintWithPerk,
  ) {
    void this.selectBlueprint(group, blueprint);
    void this.redirectAfterProductSelect();
  }

  private async redirectAfterProductSelect() {
    const user = await this.userService.currentFirebaseAuthUser();
    const isAnonymous = await this.userService.isAnonymous();

    let redirectUrl: string;
    if (user !== null && !isAnonymous) {
      redirectUrl = PURCHASE_PAGE_CHECKOUT;
    } else {
      redirectUrl = PURCHASE_PAGE_LOGIN;
    }

    this.trackingService.trackAddToCartEvent(() => {
      HtmlHelper.instance.redirectTo(redirectUrl);
    });
  }

  private async selectBlueprint(
    group: ApiContractBlueprintGroupWithPerkbase,
    blueprint: ApiContractBlueprintWithPerk,
  ) {
    const selectedProduct = this.storeSelectedProduct(group, blueprint);
    this.bindingService.updateSelectedProduct(selectedProduct);
  }

  private storeSelectedProduct(
    group: ApiContractBlueprintGroupWithPerkbase,
    blueprint: ApiContractBlueprintWithPerk,
  ): ProductInfoV2 {
    const selectedProduct: ProductInfoV2 = {
      blueprintGroupId: group.id,
      blueprintId: blueprint.id,
      bpGroupName: group.name,
      blueprintName: blueprint.name,
      price: blueprint.price,
      perk: {
        percentSaved: blueprint.perkPercent,
        priceToCompareTo:
          group.perkBasePrice &&
          blueprint.price.displayPeriod.periodIntervalCount
            ? group.perkBasePrice *
              blueprint.price.displayPeriod.periodIntervalCount
            : undefined,
      },
    };

    if (blueprint.forcedModification) {
      selectedProduct.forcedModification = blueprint.forcedModification;
    }

    this.sessionService.setObject<ProductInfoV2>(
      SessionInfo.purchase_productInfoV2,
      selectedProduct,
    );
    return selectedProduct;
  }
}
